import React, { useState, useEffect } from 'react';
import { I18nProvider, useI18n } from './core/i18n';
import { CourtSurface, UserProfile } from './core/types';
import { loadUserProfile, saveUserProfile, getTierForExp } from './core/profile';
import { BrandLogo } from './components/Logo/BrandLogo';
import { AudioControls } from './components/Audio/AudioControls';
import { CourtCustomizerModal } from './components/Modals/CourtCustomizerModal';
import { PlayerProfileModal } from './components/Profile/PlayerProfileModal';

// Views
import { CourtArenaView } from './views/CourtArenaView';
import { BaodianView } from './views/BaodianView';
import { MatchUmpireView } from './views/MatchUmpireView';
import { BiomechanicsStudioView } from './views/BiomechanicsStudioView';
import { AcademyView } from './views/AcademyView';
import { NtrpAssessmentView } from './views/NtrpAssessmentView';

import {
  Compass,
  BookOpen,
  Trophy,
  Wrench,
  GraduationCap,
  Award,
  Sliders,
  Sparkles,
} from 'lucide-react';

type TabType = 'arena' | 'baodian' | 'umpire' | 'studio' | 'academy' | 'ntrp';

const AppContent: React.FC = () => {
  const { locale, setLocale, t } = useI18n();
  const [currentTab, setCurrentTab] = useState<TabType>('arena');
  const [courtSurface, setCourtSurface] = useState<CourtSurface>('clay');
  const [showCustomizerModal, setShowCustomizerModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profile, setProfile] = useState<UserProfile>(() => loadUserProfile());

  // Keep courtSurface in sync with user profile if profile changes
  useEffect(() => {
    if (profile.courtTheme) {
      setCourtSurface(profile.courtTheme);
    }
  }, [profile.courtTheme]);

  const handleSelectSurface = (s: CourtSurface) => {
    setCourtSurface(s);
    const updated = { ...profile, courtTheme: s };
    setProfile(updated);
    saveUserProfile(updated);
  };

  const currentTier = getTierForExp(profile.exp);

  return (
    <div className="h-screen w-screen max-h-screen overflow-hidden bg-[#050b18] text-slate-100 flex flex-col selection:bg-lime-400/30 selection:text-lime-200 font-sans">
      {/* 1. Top Navbar */}
      <header className="shrink-0 h-13 sm:h-14 bg-slate-950/90 backdrop-blur-md border-b border-slate-800/80 shadow-md z-40">
        <div className="h-full max-w-7xl mx-auto px-2 sm:px-4 flex items-center justify-between">
          {/* Logo & Brand Header */}
          <div
            className="flex items-center space-x-2 cursor-pointer select-none group"
            onClick={() => setCurrentTab('arena')}
          >
            <BrandLogo size="md" />

            <div className="flex flex-col">
              <div className="text-xs sm:text-sm font-black tracking-tight text-white flex items-center gap-1.5 leading-none">
                <span>{t.appName}</span>
                <span className="text-[9px] bg-gradient-to-r from-lime-500/20 to-sky-500/20 text-lime-300 px-1.5 py-0.2 rounded-full font-bold border border-lime-500/40">
                  {t.appBadge}
                </span>
              </div>
              <div className="text-[9px] text-slate-400 font-medium leading-tight flex items-center gap-1 mt-0.5">
                <span className="text-lime-400/90 font-semibold">{t.clubName}</span>
                <span className="text-slate-600">·</span>
                <span className="hidden lg:inline text-sky-400/90">{t.sponsorName}</span>
                <span className="hidden lg:inline text-slate-600">·</span>
                <span className="hidden sm:inline text-slate-400">{t.alumniAlliance}</span>
              </div>
            </div>
          </div>

          {/* Desktop Navigation Tabs */}
          <nav className="hidden md:flex items-center space-x-1 bg-slate-900/90 p-1 rounded-2xl border border-slate-800">
            <button
              onClick={() => setCurrentTab('arena')}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-extrabold transition-all ${
                currentTab === 'arena'
                  ? 'bg-lime-500 text-slate-950 shadow-sm'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              <Compass className="w-3.5 h-3.5" />
              <span>{t.tabArena}</span>
            </button>

            <button
              onClick={() => setCurrentTab('baodian')}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-extrabold transition-all ${
                currentTab === 'baodian'
                  ? 'bg-amber-500 text-slate-950 shadow-sm'
                  : 'text-amber-300 hover:text-white'
              }`}
            >
              <BookOpen className="w-3.5 h-3.5 text-amber-400" />
              <span>{t.tabBaodian}</span>
            </button>

            <button
              onClick={() => setCurrentTab('umpire')}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-extrabold transition-all ${
                currentTab === 'umpire'
                  ? 'bg-sky-500 text-slate-950 shadow-sm'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              <Trophy className="w-3.5 h-3.5" />
              <span>{t.tabUmpire}</span>
            </button>

            <button
              onClick={() => setCurrentTab('studio')}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-extrabold transition-all ${
                currentTab === 'studio'
                  ? 'bg-emerald-500 text-slate-950 shadow-sm'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              <Wrench className="w-3.5 h-3.5" />
              <span>{t.tabStudio}</span>
            </button>

            <button
              onClick={() => setCurrentTab('academy')}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-extrabold transition-all ${
                currentTab === 'academy'
                  ? 'bg-lime-500 text-slate-950 shadow-sm'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              <GraduationCap className="w-3.5 h-3.5" />
              <span>{t.tabAcademy}</span>
            </button>

            <button
              onClick={() => setCurrentTab('ntrp')}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-extrabold transition-all ${
                currentTab === 'ntrp'
                  ? 'bg-amber-500 text-slate-950 shadow-sm'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              <Award className="w-3.5 h-3.5" />
              <span>{t.tabNtrp}</span>
            </button>
          </nav>

          {/* Right Controls: Profile pill, Audio Toggle, Language switcher */}
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            {/* Player Profile & NTRP Badge */}
            <button
              onClick={() => setShowProfileModal(true)}
              className="flex items-center space-x-1.5 bg-slate-900/90 hover:bg-slate-850 border border-slate-800 hover:border-lime-500/40 rounded-xl px-2.5 py-1 shadow-sm transition-all text-xs"
              title="个人中心与俱乐部段位"
            >
              <span className="text-sm">{currentTier.icon}</span>
              <div className="hidden sm:flex flex-col items-start leading-none text-[10px]">
                <span className="font-black text-lime-400">
                  NTRP {profile.ntrpRating.toFixed(1)} · Lv.{profile.level}
                </span>
                <span className="text-slate-400 text-[9px] mt-0.5">{currentTier.name}</span>
              </div>
            </button>

            {/* Audio SFX Toggle */}
            <AudioControls />

            {/* Language Switcher */}
            <div className="flex items-center bg-slate-900 border border-slate-800 rounded-xl p-0.5 text-[10px] font-bold">
              <button
                onClick={() => setLocale('zh_cn')}
                className={`px-1.5 py-0.5 rounded-lg transition-all ${
                  locale === 'zh_cn' ? 'bg-lime-500 text-slate-950 shadow' : 'text-slate-400 hover:text-white'
                }`}
                title="简体中文"
              >
                简
              </button>
              <button
                onClick={() => setLocale('zh_tw')}
                className={`px-1.5 py-0.5 rounded-lg transition-all ${
                  locale === 'zh_tw' ? 'bg-lime-500 text-slate-950 shadow' : 'text-slate-400 hover:text-white'
                }`}
                title="繁體中文"
              >
                繁
              </button>
              <button
                onClick={() => setLocale('en')}
                className={`px-1.5 py-0.5 rounded-lg transition-all ${
                  locale === 'en' ? 'bg-lime-500 text-slate-950 shadow' : 'text-slate-400 hover:text-white'
                }`}
                title="English"
              >
                EN
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* 2. Main Body Content Area */}
      <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
        {currentTab === 'arena' && (
          <CourtArenaView
            surface={courtSurface}
            onOpenCustomizer={() => setShowCustomizerModal(true)}
          />
        )}
        {currentTab === 'baodian' && <BaodianView />}
        {currentTab === 'umpire' && <MatchUmpireView />}
        {currentTab === 'studio' && <BiomechanicsStudioView />}
        {currentTab === 'academy' && (
          <AcademyView profile={profile} onUpdateProfile={setProfile} />
        )}
        {currentTab === 'ntrp' && (
          <NtrpAssessmentView profile={profile} onUpdateProfile={setProfile} />
        )}
      </main>

      {/* 3. Mobile Bottom Navigation Dock */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 h-14 bg-slate-950/95 backdrop-blur-lg border-t border-slate-800/90 flex items-center justify-around px-1 z-40">
        <button
          onClick={() => setCurrentTab('arena')}
          className={`flex flex-col items-center justify-center flex-1 py-1 ${
            currentTab === 'arena' ? 'text-lime-400' : 'text-slate-400'
          }`}
        >
          <Compass className="w-4 h-4" />
          <span className="text-[10px] font-bold mt-0.5">{t.tabArena}</span>
        </button>

        <button
          onClick={() => setCurrentTab('baodian')}
          className={`flex flex-col items-center justify-center flex-1 py-1 ${
            currentTab === 'baodian' ? 'text-amber-400' : 'text-slate-400'
          }`}
        >
          <BookOpen className="w-4 h-4" />
          <span className="text-[10px] font-bold mt-0.5">{t.tabBaodian}</span>
        </button>

        <button
          onClick={() => setCurrentTab('umpire')}
          className={`flex flex-col items-center justify-center flex-1 py-1 ${
            currentTab === 'umpire' ? 'text-sky-400' : 'text-slate-400'
          }`}
        >
          <Trophy className="w-4 h-4" />
          <span className="text-[10px] font-bold mt-0.5">{t.tabUmpire}</span>
        </button>

        <button
          onClick={() => setCurrentTab('studio')}
          className={`flex flex-col items-center justify-center flex-1 py-1 ${
            currentTab === 'studio' ? 'text-emerald-400' : 'text-slate-400'
          }`}
        >
          <Wrench className="w-4 h-4" />
          <span className="text-[10px] font-bold mt-0.5">{t.tabStudio}</span>
        </button>

        <button
          onClick={() => setCurrentTab('academy')}
          className={`flex flex-col items-center justify-center flex-1 py-1 ${
            currentTab === 'academy' ? 'text-lime-400' : 'text-slate-400'
          }`}
        >
          <GraduationCap className="w-4 h-4" />
          <span className="text-[10px] font-bold mt-0.5">{t.tabAcademy}</span>
        </button>

        <button
          onClick={() => setCurrentTab('ntrp')}
          className={`flex flex-col items-center justify-center flex-1 py-1 ${
            currentTab === 'ntrp' ? 'text-amber-400' : 'text-slate-400'
          }`}
        >
          <Award className="w-4 h-4" />
          <span className="text-[10px] font-bold mt-0.5">{t.tabNtrp}</span>
        </button>
      </nav>

      {/* 4. Modals */}
      <CourtCustomizerModal
        isOpen={showCustomizerModal}
        onClose={() => setShowCustomizerModal(false)}
        currentSurface={courtSurface}
        onSelectSurface={handleSelectSurface}
      />

      <PlayerProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        profile={profile}
        onUpdateProfile={setProfile}
      />
    </div>
  );
};

export const App: React.FC = () => {
  return (
    <I18nProvider>
      <AppContent />
    </I18nProvider>
  );
};

export default App;
